import type { FriendMark, OtherDate } from '~/api/types'
import { AppLink } from '~/components/AppLink'
import { Face } from '~/components/Faces'

import s from './WhosGoing.module.css'

/**
 * The three things the sheet says about a gig that are not about the gig: who else is going,
 * where else the acts on this bill are playing, and what else is on that night.
 *
 * Ported from `_calendar_page.py` — the `.whosgoing` panel inside `openEvent`, the "Also
 * playing" section built from `detail.other_dates`, and the `dupes` block of `editPanel`.
 *
 * All three are answers to "is this the one to go to", which is why they are one component
 * and why they sit above the sources rather than below them. Who is going came third from
 * bottom of the sheet for a long time; it is the reason accounts exist here at all.
 */

/**
 * The signed-in viewer. Local, like `WeekView`'s: this component needs the editor flag and
 * nothing else about them, and the payload's `me` is handed down rather than refetched.
 */
export interface Me {
  id: number
  name: string
  username: string
  isEditor: boolean
}

export interface WhosGoingProps {
  /**
   * The people going to *this* gig, from the calendar entry's own `friends`.
   *
   * `FriendMark`, not `Friend`. The friendships list is every link you have, with a status
   * and a direction and no idea which gig anybody is attending — a component handed that
   * would have shown every accepted friend as going to every event on the calendar, which
   * is not a leak so much as an invention. `people.friends_going` answers the per-event
   * question in SQL, and answers it for accepted rows only.
   */
  friends: FriendMark[]
  /** Other nights for the acts on this bill, in the server's order. */
  otherDates: OtherDate[]
  /** Open another event in the sheet. The id, never the artist. */
  onOpen: (id: string) => void
  /**
   * Which of the three to draw.
   *
   * They belong in two places in the sheet, not one. Who is going is about the evening and
   * sits with the night, above the blurb. Where else the acts play, and what else is on that
   * night, are about deciding between gigs and belong under the bill — because they only
   * mean anything once you know who is on it. Rendering all three in the first slot put
   * "Also playing" above the bill it is playing against, which is how the port left it.
   */
  sections?: ('going' | 'context')[]
  /**
   * Whether the night has already happened, so the sentence can be in the right tense.
   *
   * "Sam and Ada are going" about a gig last Tuesday is the same slip the going control had
   * beside it, in a sentence rather than on a button — and this one is about other people,
   * which makes it read as a plan they still have.
   */
  past?: boolean
}

/**
 * Only accepted friendships show anything, and this component is not where that is decided.
 *
 * A friendship is a row with a `status` from the moment it is asked for, so anything that
 * answered "who is going" from the friendships list would carry the people who have not
 * answered you — and sending a request would become a way of finding out where somebody is
 * going to be, which is the one thing the friends model exists to prevent. The view's own
 * description says so: "asking is not a way of finding out".
 *
 * `people.friends_going` does it in SQL, per event, accepted only. What arrives here has no
 * `status` to re-filter on, which is the right shape: a client cannot forget a rule it was
 * never given the data to break.
 */
function nameOf(friend: FriendMark): string {
  return friend.name
}

/**
 * A row's people as faces, every one of them, each a link to its profile.
 *
 * Not the `Faces` stack. That is one link to the gig with one sentence on it, drawn where
 * the gig is the thing to open; here the gig is already open and a face is the way to the
 * person. So each is its own anchor, none is capped or counted, and none is dimmed or
 * ringed — the sentence beside the row says which of the two things they did, and a face
 * stepped back under "saved this" would be saying it twice.
 *
 * The link's name is the friend's, said outright rather than left to the image's `alt` or
 * the plate's label, so a reader tabbing across the row hears a name whichever of the two
 * the face happens to be.
 */
function Profiles({ people }: { people: readonly FriendMark[] }) {
  return (
    <span className={s.faces}>
      {people.map(friend => (
        <AppLink
          key={friend.id}
          to={`/u/${friend.username}`}
          className={s.pface}
          aria-label={friend.name}
          title={friend.name}
          data-size={32}
        >
          <Face friend={friend} />
        </AppLink>
      ))}
    </span>
  )
}

/**
 * One block per act, in the order the server sent the dates. A `Map` keyed on the act's name
 * rather than on `artist_id` because the name is what the block is headed with — the Python
 * groups on the same key, and two ids sharing a name would read as one act twice.
 */
export function byArtist(dates: readonly OtherDate[]): [string, OtherDate[]][] {
  const groups = new Map<string, OtherDate[]>()
  for (const date of dates) {
    const run = groups.get(date.artist)
    if (run) run.push(date)
    else groups.set(date.artist, [date])
  }
  return [...groups]
}

function short(value: string | null | undefined, n: number): string {
  const text = String(value ?? '')
  if (text.length <= n) return text
  // Break at a bill separator where there is one, so the result reads as names rather than a
  // word sliced in half.
  const cut = text.slice(0, n)
  const at = cut.lastIndexOf(' + ')
  return (at > n / 2 ? cut.slice(0, at) : cut.trimEnd()) + ' …'
}

/**
 * What the act's heading says it has: "1 other date", "2 other dates".
 *
 * One count, because the server sends one kind of row: every date here is still to come,
 * cut at tonight on the same night the calendar card's "+N" is counted on. So this number
 * and the card's are the same number, and the sheet has no played half to count apart.
 */
export function datesLabel(count: number): string {
  return `${count} other date${count === 1 ? '' : 's'}`
}

export function WhosGoing({
  friends,
  otherDates,
  onOpen,
  sections = ['going', 'context'],
  past = false,
}: WhosGoingProps) {
  const going = friends
  const groups = byArtist(otherDates)
  const attending = going.filter(f => f.going)
  const keen = going.filter(f => !f.going)

  return (
    <>
      {sections.includes('going') && going.length > 0 && (
        <div className={s.whosgoing}>
          {/* Two sentences, because they are two facts. Going is about the evening; saved is
              about an intention, and reading the second as the first would have this panel
              telling you somebody will be there who has not said so. */}
          {attending.length > 0 && (
            <div className={s.row}>
              <Profiles people={attending} />
              <span>
                <b>{attending.map(nameOf).join(', ')}</b>{' '}
                {/* One word for both counts on a past night, which is the whole of the
                    agreement problem the present tense has here. */}
                {past ? 'went' : `${attending.length === 1 ? 'is' : 'are'} going`}
              </span>
            </div>
          )}
          {keen.length > 0 && (
            <div className={s.row}>
              <Profiles people={keen} />
              <span>
                <b>{keen.map(nameOf).join(', ')}</b> saved this
              </span>
            </div>
          )}
        </div>
      )}

      {sections.includes('context') && groups.length > 0 && (
        <div className={s.sec}>
          <h3>Also playing</h3>
          <div className={s.dates}>
            {groups.map(([who, list]) => (
              <div className={s.alsoblock} key={who}>
                <div className={s.alsowho}>
                  {who} — {datesLabel(list.length)}
                </div>
                {/* In the order the server sent them, which is ascending by start — the
                    right order for a list that is all ahead. */}
                {list.map(date => (
                  <button
                    type="button"
                    className={s.dline}
                    key={date.id}
                    onClick={() => onOpen(date.id)}
                  >
                    {/* `when` is formatted by the server, in London, from the same stamp the
                        rest of the payload was built with. Never re-derived here. */}
                    <span className={s.d}>{date.when}</span>
                    <span className={s.dwhere}>{date.venue}</span>
                    {/* What you are about to open. A date and a room say when and where and
                        nothing at all about which night this is — a festival day, a
                        two-act bill and a club night all read identically without it. */}
                    <span className={s.dwhat}>{short(date.title, 44)}</span>
                    {date.lifecycle_status !== 'scheduled' && (
                      <span className={`${s.tag} ${s.warn}`}>{date.lifecycle_status}</span>
                    )}
                  </button>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}

    </>
  )
}
